/**
 * **DISPOSABLE.** Pairgate probe. Removable in one commit: this page, its nav entry, its route
 * in `App.tsx`, `AdminApi.pairgateProbe`, and the Pairgate block in `contracts/pending.ts`.
 *
 * ## The question
 *
 * Pairgate document their API well, so this is not here to discover shapes. It is here because
 * of two documented facts that sit badly together for us:
 *
 * 1. A purchase carries a reference **we** choose, and repeating one is refused as a duplicate
 *    rather than replaying the original answer.
 * 2. Their transaction status lookup accepts **their** reference code only. Ours is not a
 *    lookup key anywhere in their API.
 *
 * So if a bill payment's response is lost to a timeout, the retry is refused as a duplicate
 * and we hold no code to ask about the first attempt. The customer has been debited and
 * nothing we own can say whether their token was issued.
 *
 * **This page cannot settle that, and the first version of it claimed to.** It only ever makes
 * a *test* purchase, which records no transaction, so looking that reference up finds nothing
 * whether or not our reference would work as a key for a real one. It reported `no` on that
 * basis, which agreed with what the documentation implied and was therefore not questioned.
 * The headline now says `unknown` unless a door is demonstrably open, and settling it properly
 * needs one real purchase or an answer from Pairgate.
 *
 * ## What it costs
 *
 * Nothing. Every step is a read or a purchase against Pairgate's `/test` path, which deducts
 * no balance and creates no transaction. The probe checks that claim rather than trusting it,
 * by reading the wallet balance either side.
 */
import { useEffect, useMemo, useState } from 'react';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { readPairgateProbe } from '../contracts/pending.js';
import type {
  PendingPairgateProbeResponse,
  PendingProbeStep,
  PendingProbeVerdict,
} from '../contracts/pending.js';
import { Button, Card, CopyButton, ErrorBox, Field, Input } from '../components/ui.js';

/** Their documented production host, shown so a blank field is not a guess. */
const DEFAULT_BASE_URL = 'https://pairgate.com/api/v1';

/** One scrollable, monospaced block. Inline because this page is temporary and owns no CSS. */
const OUTPUT_STYLE = {
  maxHeight: '58vh',
  overflow: 'auto',
  whiteSpace: 'pre-wrap' as const,
  wordBreak: 'break-word' as const,
  margin: 0,
  fontSize: '12px',
  lineHeight: 1.55,
};

export function PairgateProbePage(): JSX.Element {
  const { api } = useAuth();
  const [apiKey, setApiKey] = useState('');
  const [meterNumber, setMeterNumber] = useState('');
  const [providerSlug, setProviderSlug] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PendingPairgateProbeResponse | null>(null);

  // Cleared when the operator leaves. The state would be discarded anyway; saying so
  // explicitly is the point, because the handling rule for this value is that it lives for one
  // run and no longer.
  useEffect(() => () => setApiKey(''), []);

  const transcript = useMemo(() => (result === null ? '' : transcriptOf(result)), [result]);

  async function run(): Promise<void> {
    const key = apiKey.trim();
    if (key === '') {
      setError('Paste a Pairgate API key first.');
      return;
    }
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const trimmedBase = baseUrl.trim();
      const trimmedMeter = meterNumber.trim();
      const trimmedSlug = providerSlug.trim();
      const raw = await api.pairgateProbe({
        apiKey: key,
        ...(trimmedBase === '' ? {} : { baseUrl: trimmedBase }),
        ...(trimmedMeter === '' ? {} : { meterNumber: trimmedMeter }),
        ...(trimmedSlug === '' ? {} : { providerSlug: trimmedSlug }),
      });
      const parsed = readPairgateProbe(raw);
      if (parsed === null) {
        setError('The probe ran but the response could not be read. Nothing was stored.');
        return;
      }
      setResult(parsed);
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div>
      <header className="page-head">
        <h1 className="page-head__title">Pairgate probe</h1>
        <p className="page-head__sub">
          A temporary diagnostic. It runs nine calls against Pairgate, paced to stay under
          their rate limit, to learn what an adapter has to cope with. It cannot settle whether
          a payment whose response we lost can be traced, because it only makes test purchases
          and those record nothing to trace. Every step is a read or a test purchase, so it
          moves no money.
        </p>
      </header>

      <Card title="Run the probe">
        <p className="muted" style={{ maxWidth: '68ch' }}>
          The key is used for this one run and then dropped. It is sent in the request body, it
          is not saved to provider logins or anywhere else, it is kept out of the logs and the
          audit trail, and the server blanks it out of every reply Pairgate sends back. It is
          cleared from this screen when you navigate away.
        </p>

        <div style={{ display: 'grid', gap: '12px', maxWidth: '520px', marginTop: '16px' }}>
          <Field label="Pairgate API key" htmlFor="pairgate-probe-key">
            <Input
              id="pairgate-probe-key"
              type="password"
              value={apiKey}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder="one key, no secret"
              onChange={(e) => setApiKey(e.target.value)}
            />
          </Field>
          <Field label="A real meter number (optional)" htmlFor="pairgate-probe-meter">
            <Input
              id="pairgate-probe-meter"
              type="text"
              value={meterNumber}
              autoComplete="off"
              spellCheck={false}
              placeholder="leave blank to test the refusal instead"
              onChange={(e) => setMeterNumber(e.target.value)}
            />
          </Field>
          <Field label="Which disco that meter is on (optional)" htmlFor="pairgate-probe-slug">
            <Input
              id="pairgate-probe-slug"
              type="text"
              value={providerSlug}
              autoComplete="off"
              spellCheck={false}
              placeholder="e.g. ikedc, eko, aedc"
              onChange={(e) => setProviderSlug(e.target.value)}
            />
          </Field>
          <Field label="Base URL (optional)" htmlFor="pairgate-probe-base">
            <Input
              id="pairgate-probe-base"
              type="text"
              value={baseUrl}
              autoComplete="off"
              spellCheck={false}
              placeholder={DEFAULT_BASE_URL}
              onChange={(e) => setBaseUrl(e.target.value)}
            />
          </Field>
          <div>
            <Button variant="primary" loading={running} onClick={() => void run()}>
              {running ? 'Running nine calls…' : 'Run probe'}
            </Button>
          </div>
        </div>

        {error !== null && (
          <div style={{ marginTop: '16px' }}>
            <ErrorBox message={error} />
          </div>
        )}
      </Card>

      {result !== null && (
        <div style={{ marginTop: '20px' }}>
          <Card
            title="What the run answered"
            actions={<CopyButton value={transcript} label="the whole probe output" />}
          >
            {/* The headline first and on its own, because the rest is context for reading it. */}
            <p style={{ fontWeight: 600 }}>{recoverableLine(result)}</p>
            <p className="muted">{authLine(result)}</p>
            <p className="muted">{testModeLine(result)}</p>
            <p className="muted">{namespaceLine(result)}</p>
            <p className="muted">{verifyLine(result)}</p>
            {result.findings.electricitySlugs.length > 0 && (
              <p className="muted">
                Electricity providers they actually offer:{' '}
                {result.findings.electricitySlugs.join(', ')}.
              </p>
            )}
            <p className="muted">
              Ran against {result.baseUrl === '' ? DEFAULT_BASE_URL : result.baseUrl}
              {result.ranAt === null ? '' : ` at ${result.ranAt}`}. A refusal below is a result,
              not a failure: it tells us which shape to build.
            </p>
            <pre className="mono" style={OUTPUT_STYLE}>
              {transcript}
            </pre>
          </Card>
        </div>
      )}
    </div>
  );
}

/** The headline, in the operator's terms rather than the API's. */
function recoverableLine(result: PendingPairgateProbeResponse): string {
  switch (result.findings.lostResponseIsRecoverable) {
    case 'yes':
      return 'A payment whose response we lose CAN still be traced. Retrying is safe.';
    case 'no':
      return 'A payment whose response we lose CANNOT be traced from our side. A plain retry would leave a customer debited with nothing able to say what they got.';
    case 'unknown':
      return 'Whether a lost payment can be traced is STILL UNKNOWN, and this probe cannot settle it. It only ever makes a test purchase, and a test purchase records no transaction, so looking its reference up finds nothing either way. Settling it needs one real purchase, or Pairgate’s own answer.';
    default:
      return 'The run did not report an answer to the question it was built for.';
  }
}

function authLine(result: PendingPairgateProbeResponse): string {
  switch (result.findings.authenticates) {
    case 'yes':
      return 'The key was accepted.';
    case 'no':
      return 'The key was refused, so every answer below it is meaningless and the run stopped.';
    default:
      return 'Whether the key was accepted is unclear.';
  }
}

function testModeLine(result: PendingPairgateProbeResponse): string {
  switch (result.findings.testModeSpendsNothing) {
    case 'yes':
      return 'Test mode spent nothing: the wallet balance was the same either side of it.';
    case 'no':
      return 'Test mode MOVED the wallet balance, which is not what it is documented to do.';
    default:
      return 'Whether test mode spent anything could not be measured on this run.';
  }
}

function verifyLine(result: PendingPairgateProbeResponse): string {
  switch (result.findings.verifyReportsFailureInsideA200) {
    case 'yes':
      return 'A FAILED meter check arrives inside an HTTP 200, with the failure in the body. Reading the HTTP status alone would take it for a verified customer.';
    case 'no':
      return 'A failed meter check arrives as a non-200, which is the straightforward case.';
    default:
      return 'This run did not see a failed meter check, so the shape of one is still unknown.';
  }
}

function namespaceLine(result: PendingPairgateProbeResponse): string {
  switch (result.findings.testModeSharesReferenceNamespace) {
    case 'yes':
      return 'A reference used in test mode is burned for live too, so test runs consume references real deliveries would need.';
    case 'no':
      return 'Test mode keeps no reference ledger, so a test run cannot collide with a real delivery.';
    default:
      return 'Whether test mode shares the live reference namespace is still unknown.';
  }
}

function transcriptOf(result: PendingPairgateProbeResponse): string {
  const verdict = (label: string, value: PendingProbeVerdict): string =>
    `${label}: ${value ?? 'not reported'}`;

  const header = [
    `Pairgate probe against ${result.baseUrl === '' ? DEFAULT_BASE_URL : result.baseUrl}`,
    result.ranAt === null ? null : `Ran at ${result.ranAt}`,
    'The API key was redacted by the server before this was sent.',
    '',
    verdict('Lost response is recoverable', result.findings.lostResponseIsRecoverable),
    verdict('Authenticates', result.findings.authenticates),
    verdict('Test mode spends nothing', result.findings.testModeSpendsNothing),
    verdict(
      'Test mode shares the live reference namespace',
      result.findings.testModeSharesReferenceNamespace,
    ),
    verdict(
      'A failed verify arrives inside a 200',
      result.findings.verifyReportsFailureInsideA200,
    ),
    result.findings.electricitySlugs.length === 0
      ? null
      : `Electricity slugs: ${result.findings.electricitySlugs.join(', ')}`,
  ].filter((line): line is string => line !== null);

  const blocks = result.steps.map((step: PendingProbeStep, index: number) => {
    const method = step.method === '' ? '' : `${step.method} `;
    const status = step.status === 0 ? 'no response' : `HTTP ${String(step.status)}`;
    const lines = [
      `=== ${String(index + 1)}. ${step.label} ===`,
      `${method}${step.path}`,
      status,
      step.error === null ? null : `not run: ${step.error}`,
      step.body === '' ? '(empty body)' : step.body,
    ].filter((line): line is string => line !== null);
    return lines.join('\n');
  });

  return [header.join('\n'), ...blocks].join('\n\n');
}
